import React, { useState } from 'react';
import { Stack, TextInput, ActionIcon, Group, Text, Progress, Loader } from '@mantine/core';
import { Plus } from 'lucide-react';
import { SubTaskItem } from './SubTaskItem';
import { SubTask } from '../types/todo';
import { useCreateSubTask, useSubTasks } from '../hooks/useSubTasks';
import './SubTasksList.css';

interface SubTasksListProps {
  parentTodoItemId: number;
  subTasks?: SubTask[]; // Make this optional for backwards compatibility
  isPublicView?: boolean;
}

export const SubTasksList: React.FC<SubTasksListProps> = ({ 
  parentTodoItemId, 
  subTasks: fallbackSubTasks = [],
  isPublicView = false 
}) => {
  const [newSubTaskTitle, setNewSubTaskTitle] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  
  // Fetch fresh sub-tasks data
  const { data: fetchedSubTasks, isLoading } = useSubTasks(parentTodoItemId);
  const createMutation = useCreateSubTask();

  // Use fetched data if available, otherwise fall back to prop data
  const subTasks = fetchedSubTasks || fallbackSubTasks;

  const completedCount = subTasks.filter((st: SubTask) => st.isCompleted).length;
  const totalCount = subTasks.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleAddSubTask = () => {
    if (newSubTaskTitle.trim()) {
      createMutation.mutate(
        {
          title: newSubTaskTitle.trim(),
          parentTodoItemId
        },
        {
          onSuccess: () => {
            setNewSubTaskTitle('');
            setIsAdding(false);
          },
          onError: () => {
            // Error handling is done in the hook
          }
        }
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddSubTask();
    } else if (e.key === 'Escape') {
      setNewSubTaskTitle('');
      setIsAdding(false);
    }
  };

  // Show loader during initial fetch
  if (isLoading) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }

  // Don't render anything if there are no sub-tasks and it's a public view
  if (totalCount === 0 && isPublicView) {
    return null;
  }

  return (
    <Stack gap="xs" className="subtasks-list">
      {/* Progress indicator */}
      {totalCount > 0 && (
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {completedCount}/{totalCount} completed
          </Text>
          <Progress 
            value={progressPercent} 
            size="sm" 
            style={{ flex: 1 }}
            color={progressPercent === 100 ? 'green' : 'blue'}
          />
        </Group>
      )}

      {/* Sub-tasks list */}
      {subTasks.length > 0 && (
        <Stack gap={4}>
          {subTasks.map((subTask: SubTask) => (
            <SubTaskItem 
              key={subTask.id} 
              subTask={subTask}
              isPublicView={isPublicView}
            />
          ))}
        </Stack>
      )}

      {/* Add new sub-task */}
      {!isPublicView && (
        <>
          {isAdding ? (
            <Group gap="xs" wrap="nowrap">
              <TextInput
                value={newSubTaskTitle}
                onChange={(e) => setNewSubTaskTitle(e.target.value)}
                onKeyDown={handleKeyPress}
                onBlur={() => {
                  if (!newSubTaskTitle.trim()) {
                    setIsAdding(false);
                  }
                }}
                placeholder="Sub-task title"
                autoFocus
                size="sm"
                style={{ flex: 1 }}
              />
              <ActionIcon
                size="sm"
                variant="filled"
                color="blue"
                onClick={handleAddSubTask}
                disabled={!newSubTaskTitle.trim() || createMutation.isPending}
                loading={createMutation.isPending}
              >
                <Plus size={16} />
              </ActionIcon>
            </Group>
          ) : (
            <Group 
              gap="xs" 
              className="add-subtask-button"
              onClick={() => setIsAdding(true)}
            >
              <Plus size={16} />
              <Text size="sm" c="dimmed">Add sub-task</Text>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};
